import { Fragment, lazy, PropsWithChildren, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import CvrScreenView from '../views/cvr/CvrScreen.view';
import ReceiptScreenView from '../views/receipt/ReceiptScreen.view';
import CallMeScreenView from '../views/call-me/CallMeScreen.view';
import CallMeReceiptView from '../views/call-me-receipt/CallMeReceipt.view';
import PageNotFoundScreenView from '../views/pageNotFound/PageNotFoundScreen.view';
import DynamicModalScreenView from '../views/dynamic-modal/DynamicModalScreen.view';
import { useGlobalDispatch, useGlobalState } from '../common/context/AppContext';
import { saveToObjectCache } from '../common/actions/ObjectCacheActions';
import { IAppProps, REDUCER_KEY } from '../common/interface/IAppProvider';
import RouterCondition from './RouterCondition';
import usePreviousRoute from '../common/customHooks/usePreviousRoute';
import MockRouteViewScreen from '../views/mock-route-view/MockRouteViewScreen';
import { track } from 'c9-tracking';
import { removeLastSlash } from '../common/utils/helpers/GenericHelpers';
import { WhitelabelPartner } from '../common/interface/IAppConfig';

const CompanyInfoScreenView = lazy(
    () => import(/*webpackChunkName: 'CompanyInfoScreenView'*/ '../views/company-info/CompanyInfoScreen.view'),
);
const OtherIndustryQuestionView = lazy(
    () =>
        import(
            /*webpackChunkName: 'OtherIndustryScreenView'*/ '../views/other-industry-question/OtherIndustryQuestion.view'
        ),
);
const OwnerView = lazy(() => import(/*webpackChunkName: 'OwnerView'*/ '../views/owner/Owner.view'));
const TurnoverView = lazy(() => import(/*webpackChunkName: 'TurnoverView'*/ '../views/turnover/Turnover.view'));
const BusinessLineQuestionView = lazy(
    () =>
        import(
            /*webpackChunkName: 'BusinessLineQuestionView'*/ '../views/business-line-question/BusinessLineQuestion.view'
        ),
);
const ClaimHistoryView = lazy(
    () => import(/*webpackChunkName: 'ClaimHistoryView'*/ '../views/claim-history/ClaimHistory.view'),
);
const PackageScreenView = lazy(
    () => import(/*webpackChunkName: 'PackageScreenView'*/ '../views/package/PackageScreen.view'),
);
const AdditionalCoveragesScreenView = lazy(
    () => import('../views/additional-coverages/AdditionalCoveragesScreen.view'),
);
const BasketScreenView = lazy(
    () => import(/*webpackChunkName: 'BasketScreenView'*/ '../views/basket/BasketScreen.view'),
);
const ContactInfoScreenView = lazy(
    () => import(/*webpackChunkName: 'ContactInfoScreenView'*/ '../views/contact-info/ContactInfoScreen.view'),
);
const StartDateScreenView = lazy(
    () => import(/*webpackChunkName: 'StartDateScreenView'*/ '../views/start-date/StartDateScreen.view'),
);
const PaymentOptionsScreenView = lazy(
    () => import(/*webpackChunkName: 'PaymentOptionsScreenView'*/ '../views/payment-options/PaymentOptionsScreen.view'),
);
const ComplianceScreenView = lazy(
    () => import(/*webpackChunkName: 'ComplianceScreenView'*/ '../views/compliance/ComplianceScreen.view'),
);
const QuoteScreenView = lazy(() => import(/*webpackChunkName: 'QuoteScreenView'*/ '../views/quote/QuoteScreen.view'));

interface IError {
    rmo: boolean;
    rmoReceipt: boolean;
}

interface IModal {
    readonly props: PropsWithChildren<IAppProps>;
    readonly location: any;
    readonly error: IError;
    rmoState: (key: string, val: boolean) => void;
}

export enum ROUTE_NAMES {
    // MUST be in order of flow
    CVR = '/', // CvrScreenView
    COMPANY_INFO = '/virksomhedsinformation', // CompanyInfoScreenView
    OTHER_INDUSTRY = '/andre-brancher', // OtherIndustryScreenView
    OWNER = '/ejere', // OwnerScreenView
    TURNOVER = '/omsaetning', // TurnoverView
    BUSINESS_LINE_QUESTION = '/afklaring', // BusinessLineQuestionView
    PACKAGE = '/pakke', // PackageScreenView
    ADDITIONAL_COVERAGES = '/tilvalg',
    BASKET = '/indkoebskurv', // BasketScreenView
    CLAIM_HISTORY = '/skader', // ClaimHistoryView
    CONTACT_INFO = '/kontaktoplysninger', // ContactInfoScreenView
    START_DATE = '/startdato', // StartDateScreenView
    PAYMENT = '/betaling', // PaymentOptionsScreenView
    COMPLIANCE = '/accept', // ComplianceScreenView
    QUOTE_INFO = '/bestil-tilbud', // QuoteScreenView
    RECEIPT = '/', // ReceiptScreenView
    CALL_ME = '/ring-mig-op', // CallMeScreenView
    CALL_ME_RECEIPT = '/kvittering-rmo', // CallMeReceiptView
    PAGE_NOT_FOUND = '/404', //PageNotFoundScreenView
}

export const fallBack = <Fragment />;

export default function RouterApp(props: PropsWithChildren<IAppProps>): JSX.Element {
    const location = useLocation();
    const navigate = useNavigate();

    const globalState = useGlobalState();
    const appDispatch = useGlobalDispatch();

    const [error, setError] = useState<IError>({ rmo: false, rmoReceipt: false });
    const [currentLocation, setCurrentLocation] = useState(location);
    const [isUpdating, setIsUpdating] = useState(false);
    const [initPartner, setInitPartner] = useState<boolean>(true);

    const { search } = location;
    const prevLocation = usePreviousRoute(location);
    const isReceipt = props.config.baseName.indexOf('kvittering') !== -1;

    const changeState = (key: string, val: boolean) => {
        setError((prev) => ({ ...prev, [key]: val }));
    };

    // Check if the flow was initialized as a whitelabel/partner flow
    useEffect(() => {
        const mapPartner = (name: string | null): WhitelabelPartner => {
            if (!name) {
                return WhitelabelPartner.NONE;
            }

            switch (name.toLowerCase()) {
                case 'dbbr':
                    return WhitelabelPartner.DANSK_BILBRANCHERÅD;
                case 'nordea':
                    return WhitelabelPartner.NORDEA;
                case 'dinero':
                    return WhitelabelPartner.DINERO;
                default:
                    return WhitelabelPartner.NONE;
            }
        };

        if (initPartner) {
            setInitPartner(false);
            const partner = new URLSearchParams(search).get('partner');
            if (partner !== null) {
                track.info(`partner: ${partner}`);
                props.c9log.info(`partner: ${partner}`);
                appDispatch({ type: REDUCER_KEY.SET_PARTNER, payload: mapPartner(partner) });
            }
        }
    }, [initPartner, search, appDispatch, props.c9log]);

    useEffect(() => {
        if (!globalState.initialState && globalState.stateChange && !isUpdating) {
            saveToObjectCache(props, globalState, appDispatch);
            setIsUpdating(true);
        } else if (!globalState.stateChange && isUpdating) {
            setIsUpdating(false);
        }
    }, [props, appDispatch, globalState, globalState.stateChange, globalState.initialState, isUpdating]);

    useEffect(() => {
        // prevent multiple redirects from happen
        if (
            !globalState.stateChange &&
            error.rmoReceipt &&
            !globalState.hasError.stepOne &&
            !globalState.hasError.stepTwo &&
            !isReceipt
        ) {
            const endsWithSlash = /\/$/;
            let bn = props.config.baseName.match(endsWithSlash)
                ? props.config.baseName.slice(0, -1)
                : props.config.baseName;
            setTimeout(() => {
                window.location.assign(`${bn}/kvittering-tilbud/`);
            }, 300);
        }
    }, [error.rmoReceipt, props.config.baseName, globalState.hasError, globalState.stateChange, isReceipt]);

    useEffect(() => {
        if (prevLocation !== location) {
            const index = Object.keys(ROUTE_NAMES).findIndex((key: string) => {
                // @ts-ignore
                return ROUTE_NAMES[key] === location.pathname;
            });
            if (prevLocation && (prevLocation as any).pathname !== ROUTE_NAMES.CALL_ME) {
                appDispatch({ type: REDUCER_KEY.SET_PREV_ROUTE_CONFIG, payload: prevLocation });
            }
            appDispatch({ type: REDUCER_KEY.SET_ACTUAL_STEP, payload: index + 1 });

            //important: handle state object for browser forward disable
            //Need to replace history if obsolete property exist in state
            if (typeof location.state == 'object' && location.state && (location.state as any).obsolete) {
                let newLocation = { ...location, state: undefined };
                navigate(newLocation, { replace: true });
            }
        }

        if (
            prevLocation !== location &&
            !(
                location.pathname === '/kvittering-koeb/' ||
                location.pathname === '/kvittering-tilbud/' ||
                location.pathname === '/'
            ) &&
            !isReceipt
        ) {
            track.navigate(
                document.URL,
                `Show ${
                    location.pathname === '/' ? 'adresse' : removeLastSlash(location.pathname)
                } view to the customer`,
            );
        }
    }, [prevLocation, location, isReceipt, navigate, appDispatch]);

    useEffect(() => {
        if (!error.rmo && (globalState.hasError.stepOne || globalState.hasError.separateCallMe) && !isReceipt) {
            changeState('rmo', true);
            if (globalState.hasError.stepOne) {
                setCurrentLocation((prevState: any) => ({
                    ...prevState,
                    state: { ...prevState.state, modal: globalState.prevLocation || prevLocation },
                }));
            }
            //important: Condition to check browser refresh with RMO
            if (location.pathname !== ROUTE_NAMES.CALL_ME) {
                navigate(ROUTE_NAMES.CALL_ME);
            }
        }
    }, [
        appDispatch,
        error.rmo,
        globalState.hasError.separateCallMe,
        globalState.hasError.stepOne,
        location,
        prevLocation,
        globalState.prevLocation,
        isReceipt,
        navigate,
    ]);

    useEffect(() => {
        if (location.pathname !== ROUTE_NAMES.CALL_ME) {
            setCurrentLocation((prevState: any) => ({
                ...prevState,
                state: undefined,
            }));
        }
    }, [location]);

    useEffect(() => {
        if (
            !error.rmoReceipt &&
            globalState.hasError.stepTwo &&
            globalState.hasError.separateCallMe &&
            !globalState.stateChange
        ) {
            appDispatch({
                type: REDUCER_KEY.SET_ERROR,
                payload: { stepOne: false, stepTwo: false, separateCallMe: false },
            });
            appDispatch({ type: REDUCER_KEY.SET_STATE_CHANGE, payload: true });
            changeState('rmoReceipt', true);
        }
    }, [
        globalState.hasError.stepTwo,
        globalState.hasError.separateCallMe,
        globalState.stateChange,
        appDispatch,
        error.rmoReceipt,
        globalState.hasError,
    ]);

    return <ModalSwitch props={props} location={currentLocation} rmoState={changeState} error={error} />;
}

function ModalSwitch(props: PropsWithChildren<IModal>) {
    const { hasError } = useGlobalState();
    let location = useLocation();
    let showModal = props.location.state && props.location.state.modal;
    const isReceipt = props.props.config.baseName.indexOf('kvittering') !== -1;

    if (showModal) {
        if (hasError.stepOne) {
            location = { ...location, pathname: ROUTE_NAMES.CALL_ME };
        } else if (hasError.stepTwo && !hasError.separateCallMe) {
            location = { ...location, pathname: ROUTE_NAMES.CALL_ME_RECEIPT };
        }
        location = { ...location, state: props.location.state };
    }

    return (
        <>
            <Routes location={showModal || location}>
                <Route element={<RouterCondition {...props.props} />}>
                    <Route
                        path={isReceipt ? ROUTE_NAMES.RECEIPT : ROUTE_NAMES.CVR}
                        element={
                            <>
                                {isReceipt ? (
                                    <Suspense fallback={fallBack}>
                                        <ReceiptScreenView {...props.props} />
                                    </Suspense>
                                ) : (
                                    <Suspense fallback={fallBack}>
                                        <CvrScreenView {...props.props} />
                                    </Suspense>
                                )}
                            </>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.COMPANY_INFO}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <CompanyInfoScreenView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.OWNER}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <OwnerView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.OTHER_INDUSTRY}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <OtherIndustryQuestionView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.TURNOVER}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <TurnoverView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.BUSINESS_LINE_QUESTION}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <BusinessLineQuestionView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.CLAIM_HISTORY}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <ClaimHistoryView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.QUOTE_INFO}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <QuoteScreenView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.PACKAGE}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <PackageScreenView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.ADDITIONAL_COVERAGES}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <AdditionalCoveragesScreenView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.BASKET}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <BasketScreenView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.CONTACT_INFO}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <ContactInfoScreenView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.START_DATE}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <StartDateScreenView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.PAYMENT}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <PaymentOptionsScreenView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.COMPLIANCE}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <ComplianceScreenView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.CALL_ME}`}
                        element={
                            <>
                                {hasError.separateCallMe ? (
                                    <Suspense fallback={fallBack}>
                                        <CallMeScreenView {...props.props} />
                                    </Suspense>
                                ) : (
                                    <MockRouteViewScreen />
                                )}
                            </>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.CALL_ME_RECEIPT}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <CallMeReceiptView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route
                        path={`${ROUTE_NAMES.PAGE_NOT_FOUND}`}
                        element={
                            <Suspense fallback={fallBack}>
                                <PageNotFoundScreenView {...props.props} />
                            </Suspense>
                        }
                    />
                    <Route path="*" element={<Navigate to={`${ROUTE_NAMES.PAGE_NOT_FOUND}`} />} />
                </Route>
            </Routes>

            {showModal && (
                <>
                    <Route
                        path={`${ROUTE_NAMES.CALL_ME}`}
                        children={({ match }: { match: boolean }) => (
                            <>{match && <DynamicModalScreenView {...props.props} rmoState={props.rmoState} />}</>
                        )}
                    />
                    <Route path={`${ROUTE_NAMES.CALL_ME_RECEIPT}`}>
                        <DynamicModalScreenView {...props.props} rmoState={props.rmoState} />
                    </Route>
                </>
            )}
        </>
    );
}
